import { useEffect, useMemo, useRef, useState } from 'react';
import type { Account } from '@accounting/shared';
import { buildAccountOptions } from '../../lib/accountOptions';

/** One line in the menu: an account option, or the optional "none" row (id ''). */
interface PickerRow {
  id: string;
  label: string;
}

interface Props {
  /** FULL accounts list (don't pre-filter — pass `predicate` instead so the
   *  parent path is still computed via filtered-out header accounts). */
  accounts: Account[];
  value: string;
  onChange: (accountId: string) => void;
  /** Visibility filter. Default: include everything. */
  predicate?: (a: Account) => boolean;
  placeholder?: string;
  /**
   * When set, the list starts with a "none" row carrying this label. Picking it
   * calls onChange('') so an optional field (a parent account, say) can be
   * cleared again after something was chosen. Without it there is no way back
   * to empty: typing over the label just restores it on blur.
   */
  noneLabel?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  style?: React.CSSProperties;
  className?: string;
  /** Optional title shown on the input element. */
  title?: string;
}

/**
 * Searchable account picker. Renders the chosen account as a "Code — Parent:Child"
 * label, opens a filterable dropdown on focus/click, and matches typed text against
 * code / name / full path via case-insensitive substring.
 */
export function AccountPicker({
  accounts,
  value,
  onChange,
  predicate,
  placeholder = '— select account —',
  noneLabel,
  disabled,
  required,
  id,
  style,
  className,
  title,
}: Props) {
  const options = useMemo(() => buildAccountOptions(accounts, predicate), [accounts, predicate]);
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const selected = value ? byId.get(value) : undefined;
  const selectedLabel = selected?.label ?? '';

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selectedLabel);
  const [highlighted, setHighlighted] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Mirror the selected label into the input when the menu is closed (so external
  // changes to `value`, or restoring after Esc, show the right text).
  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [selectedLabel, open]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery(selectedLabel);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, selectedLabel]);

  // Rows in the menu: the optional "none" row first, then the matching accounts.
  const filtered = useMemo<PickerRow[]>(() => {
    const noneRow: PickerRow | null = noneLabel !== undefined ? { id: '', label: noneLabel } : null;
    const withNone = (rows: PickerRow[]) => (noneRow ? [noneRow, ...rows] : rows);
    if (!open) return withNone(options);
    const q = query.trim().toLowerCase();
    // If the input still shows the selected label, treat the query as empty so
    // the user sees the full list immediately on focus.
    if (!q || q === selectedLabel.toLowerCase()) return withNone(options);
    const matches: PickerRow[] = options.filter((o) => o.label.toLowerCase().includes(q));
    return noneRow && noneRow.label.toLowerCase().includes(q) ? [noneRow, ...matches] : matches;
  }, [open, options, query, selectedLabel, noneLabel]);

  useEffect(() => {
    setHighlighted(0);
  }, [filtered.length, open]);

  const openMenu = () => {
    if (disabled || open) return;
    setOpen(true);
    const idx = filtered.findIndex((o) => o.id === value);
    setHighlighted(idx >= 0 ? idx : 0);
    // Select all so a keystroke immediately replaces the label.
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const pick = (o: PickerRow) => {
    onChange(o.id);
    // The "none" row leaves the box empty so the placeholder shows again.
    setQuery(o.id ? o.label : '');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (!open) {
        openMenu();
        return;
      }
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlighted]) {
        e.preventDefault();
        pick(filtered[highlighted]!);
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      setQuery(selectedLabel);
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQuery(selectedLabel);
    }
  };

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlighted] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', ...style }} className={className}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={query}
        onChange={(e) => {
          if (!open) setOpen(true);
          setQuery(e.target.value);
        }}
        onFocus={openMenu}
        onClick={openMenu}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        title={title}
        // Mark required only when no value is set; the displayed label keeps the
        // field non-empty when something is selected.
        required={required && !value}
        autoComplete="off"
        spellCheck={false}
        style={{ width: '100%' }}
      />
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 50,
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 2,
            padding: 4,
            listStyle: 'none',
            background: 'var(--bg-surface)',
            color: 'var(--text-default)',
            border: '1px solid var(--border, #d4dbe5)',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 && (
            <li className="muted" style={{ padding: '6px 10px', fontSize: 13 }}>
              No accounts match.
            </li>
          )}
          {filtered.map((o, i) => (
            <li
              key={o.id}
              role="option"
              aria-selected={o.id === value}
              // mousedown (not click) + preventDefault so the input's blur
              // doesn't close the menu before the selection registers.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                padding: '4px 10px',
                cursor: 'pointer',
                fontSize: 13,
                borderRadius: 4,
                background: i === highlighted ? 'var(--bg-surface-hover)' : 'transparent',
                fontWeight: o.id === value ? 600 : 400,
                fontStyle: o.id === '' ? 'italic' : 'normal',
              }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
